// src/pages/CheckoutPage.jsx
import { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, IconButton, Grid, TextField, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, TableFooter, Divider, useMediaQuery } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { getCurrentFYear } from '../utils/financialYear';

const API_URL = 'http://192.168.1.248:3103/api';

export default function CheckoutPage({ loggedInUser, companyData }) {
    const { cartItems, total, clearCart } = useCart();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const [loading, setLoading] = useState(false);
    const [firmSearch, setFirmSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [selectedFirm, setSelectedFirm] = useState(null);
    const [bill, setBill] = useState({ firm: '', address: '', city: '', state: '', pin: '', gst: '' });
    const [ship, setShip] = useState({ firm: '', address: '', city: '', state: '', pin: '', gst: '' });
    const [discount, setDiscount] = useState(0);
    const [companyState, setCompanyState] = useState('');
    const [normalizedState, setNormalizedState] = useState('');

    useEffect(() => {
        const cart = JSON.parse(localStorage.getItem("cart")) || [];
        const orders = JSON.parse(localStorage.getItem("cartOrder")) || [];

        console.log("🛒 Full Cart Items:", cart);
        console.log("📑 Orders:", orders);

        // Safely extract first-level product fields
        Object.values(cart).forEach(({ product }, index) => {
            const itemCode = product?.itemCode || '';
            const colorId = product?.colorId || '';
            const sizeId = product?.sizeId || '';
            const itemGroup = product?.itemGroup || '';

            console.log(`🧩 Cart Item ${index + 1}`);
            console.log("itemCode:", itemCode);
            console.log("colorId:", colorId);
            console.log("sizeId:", sizeId);
            console.log("itemGroup:", itemGroup);
        });
    }, []);

    useEffect(() => {
        if (companyData?.[0]?.state) {
            setCompanyState(companyData[0].state.trim().toLowerCase());
        }
    }, [companyData]);

    useEffect(() => {
        setNormalizedState(bill.state.trim().toLowerCase());
    }, [bill.state]);

    const debouncedSearch = useMemo(() => debounce(async val => {
        try {
            const { data } = await axios.get(`${API_URL}/company-search`, { params: { field: 'companyName', search: val } });;
            setSearchResults(data.data || []);
            setShowResults(true);
        } catch {
            setSearchResults([]);
        }
    }, 300), []);

    useEffect(() => {
        if (firmSearch.trim()) debouncedSearch(firmSearch.trim());
        else {
            setShowResults(false);
            setSearchResults([]);
        }
    }, [firmSearch]);

    const onSelect = c => {
        setSelectedFirm(c);
        console.log(c);
        setFirmSearch(c.clientname);
        setShowResults(false);
        const mapped = {
            firm: c.clientname, address: c.address, city: c.city,
            state: c.state, pin: c.pinCode?.toString(), gst: c.gstno,
            email: c.email || '',       // ✅ added
            mobile: c.mobile || '',     // ✅ added
        };
        setBill(mapped);
        setShip(mapped);
    };

    const placeOrder = async () => {
        setLoading(true);

        const isIntra = normalizedState === companyState;

        const basic = total;
        const discountAmt = basic * (discount / 100);
        const discounted = basic - discountAmt;

        const gstTotal = Object.values(cartItems).reduce((sum, { product, qty }) => {
            const base = product.saleRate * qty;
            const discountAmt = base * (discount / 100);
            const taxable = base - discountAmt;
            return sum + ((product.TaxGST / 100) * taxable);
        }, 0);

        const rawFinal = discounted + gstTotal;
        const roundedFinal = Math.ceil(rawFinal);
        const roundOff = +(roundedFinal - rawFinal).toFixed(2);

        const masterPayload = {
            // === saleordermaster table ===
            CompanyID: companyData?.[0]?.companyId,
            BookName: "SalesOrder",                  // Optional: Update if you use a specific book
            Prefix: "SO",                              // If applicable
            Suffix: getCurrentFYear(),
            SONumber: "",                            // Will be auto-generated if logic is on backend
            SODate: new Date(),
            DeliveryDate: new Date(),
            PartyID: selectedFirm?.id || null, // Should come from search result
            PartyName: selectedFirm?.clientname || "",  // ✅ Firm name as PartyName

            // === Additional address details ===
            companyName: selectedFirm?.clientname || "",   // Optional duplicate if needed
            city: bill?.city || "",
            state: bill?.state || "",
            pinCode: bill?.pin || "",                      // You'll store this in a new DB column if needed
            address: bill?.address || "",
            gstNumber: bill?.gst || "",
            emailId: bill?.email || "",       // ✅ added
            mobileNo: bill?.mobile || "",     //

            ConsigneeID: selectedFirm?.id,                       // If different, else null or same as PartyID
            BasicAmt: +basic.toFixed(2),
            Disc: +discount.toFixed(2),
            DiscAmt: +discountAmt.toFixed(2),
            CGST: isIntra ? 9 : 0,
            CGSTAmt: isIntra ? +(gstTotal / 2).toFixed(2) : 0,
            SGST: isIntra ? 9 : 0,
            SGSTAmt: isIntra ? +(gstTotal / 2).toFixed(2) : 0,
            IGST: isIntra ? 0 : 18,
            IGSTAmt: isIntra ? 0 : +gstTotal.toFixed(2),
            RoundOff: +roundOff.toFixed(2),
            BillAmt: +roundedFinal.toFixed(2),
            addUserId: loggedInUser?.userId,
            addUserDateTime: new Date(),
            fYear: getCurrentFYear(),
            branchId:  companyData?.[0]?.companyId|| 1,
            groupOfCompanyId:  companyData?.[0]?.companyId || 1,
        };

        const detailPayload = Object.values(cartItems).map(({ product, qty }, index) => {
            const base = product.saleRate * qty;
            const discAmt = base * (discount / 100);
            const taxable = base - discAmt;

            // ✅ Define these here inside the map function
            const itemCode = product?.itemCode || '';
            const colorId = product?.colorId || '';
            const sizeId = product?.sizeId || '';
            const itemGroup = product?.itemGroup || '';

            return {
                // === saleorderdetails table ===
                SrNo: index + 1,
                PartyID:  selectedFirm?.id || null,
                ItemID: product.Id,
                Unit: product.unit,
                Qty: qty,
                FreeQty: 0,
                Rate: product.saleRate,
                Per: product.unit,                            // Assuming per rate is per piece
                Disc: +discount.toFixed(2),
                DiscAmt: +discAmt.toFixed(2),
                CGST: isIntra ? +(product.TaxGST / 2).toFixed(2) : 0,
                CGSTAmt: isIntra ? +(taxable * (product.TaxGST / 2 / 100)).toFixed(2) : 0,
                SGST: isIntra ? +(product.TaxGST / 2).toFixed(2) : 0,
                SGSTAmt: isIntra ? +(taxable * (product.TaxGST / 2 / 100)).toFixed(2) : 0,
                IGST: isIntra ? 0 : +product.TaxGST,
                IGSTAmt: isIntra ? 0 : +(taxable * (product.TaxGST / 100)).toFixed(2),
                Remark: "",
                // ✅ newly added fields
                ItemType: itemGroup,
                ItemSize: sizeId,
                ItemColor: colorId,
                SerialNo: itemCode,
                // metadata
                CompanyID: companyData?.[0]?.companyId,
                addUserId: loggedInUser?.userId,
                addUserDateTime: new Date(),
                fYear: getCurrentFYear(),
                branchId: companyData?.[0]?.branchId || 1,
                groupOfCompanyId: companyData?.[0]?.groupOfCompanyId || 1,
            };
        });

        const payload = {
            master: masterPayload,
            details: detailPayload,
        };

        console.log(payload);

        try {
            const response = await axios.post(`${API_URL}/create-order`, payload);
            if (response.status === 201) {
                clearCart();
                navigate(`/main/orderconfirmation/${response.data.orderId}`);
            } else {
                console.error("Order not created, status:", response.status);
            }
        } catch (err) {
            console.error("Order placement failed:", err);
        } finally {
            setLoading(false);
        }
        // setLoading(false);
    };

    return (
        <Box p={3} mx="auto">
            {/* Header */}
            <Box display="flex" alignItems="center" mb={2}>
                <IconButton onClick={() => navigate('/main/cart-page')}>
                    <ArrowBackIcon fontSize="large" />
                </IconButton>
                <Typography variant="h4" ml={2} fontWeight={700} color="primary">
                    Checkout
                </Typography>
            </Box>

            {/* Two‑Column Layout */}
            <Grid container spacing={3}>
                {/* LEFT: Billing + Shipping */}
                <Grid item xs={12} md={5} display="flex" flexDirection="column" gap={2}>
                    {/* Bill To */}
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 5, }} >
                        <Box display="flex" alignItems="center" mb={2}>
                            <Typography variant="h6" fontWeight={600} color="text.primary" flexGrow={1}>
                                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                                Bill To
                            </Typography>
                            <Divider flexItem sx={{ borderColor: 'primary.light' }} />
                        </Box>

                        <Box position="relative" mb={2}>
                            <TextField fullWidth variant="outlined" label="Firm Name" placeholder="Search company…" value={firmSearch}
                                onChange={e => {
                                    setFirmSearch(e.target.value);
                                    setSelectedFirm(null);
                                }}
                                InputProps={{ startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />, }}
                            />

                            {showResults && isDesktop && (
                                <TableContainer component={Paper} sx={{ position: 'absolute', top: '100%', mt: 1, zIndex: 20, width: '100%', boxShadow: 4, }} >
                                    <Table size="small">
                                        <TableBody>
                                            {searchResults.length ? (
                                                searchResults.map((c, i) => (
                                                    <TableRow key={i} hover onClick={() => onSelect(c)} sx={{ cursor: 'pointer', bgcolor: i % 2 ? 'background.paper' : 'grey.50', }} >
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {c.clientname} <Typography component="span" color="text.secondary">({c.city})</Typography>
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell>
                                                        <Typography variant="body2" color="text.secondary">
                                                            No Results
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>

                        <Grid container spacing={2}>
                            {['address', 'city', 'state', 'pin', 'GST'].map((field, i) => (
                                <Grid key={field} item xs={12} sm={i ? 6 : 12}>
                                    <TextField fullWidth variant="outlined" label={field.charAt(0).toUpperCase() + field.slice(1)} value={bill[field]} onChange={e => setBill(prev => ({ ...prev, [field]: e.target.value }))} size="small" />
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>

                    {/* Ship To */}
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 5, }} >
                        <Box display="flex" alignItems="center" mb={2}>
                            <Typography variant="h6" fontWeight={600} color="text.primary" flexGrow={1}>
                                <LocalShippingIcon sx={{ mr: 1, color: 'primary.main' }} />
                                Ship To
                            </Typography>
                            <Divider flexItem sx={{ borderColor: 'primary.light' }} />
                        </Box>

                        <Grid container spacing={2}>
                            {['firm', 'address', 'city', 'state', 'pin', 'GST'].map((field, i) => (
                                <Grid key={field} item xs={12} sm={i ? 6 : 12}>
                                    <TextField fullWidth variant="outlined" label={field === 'firm' ? 'Firm Name' : field.charAt(0).toUpperCase() + field.slice(1)} value={ship[field]} onChange={e => setShip(prev => ({ ...prev, [field]: e.target.value }))} size="small" />
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Grid>

                {/* RIGHT: Order Summary & Totals */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        {/* Summary Header */}
                        <Box px={3} py={2} bgcolor="primary.main" display="flex" alignItems="center" >
                            <Typography variant="h6" fontWeight={600} color="primary.contrastText">
                                Order Summary
                            </Typography>
                        </Box>

                        {/* Items Table */}
                        <TableContainer sx={{ maxHeight: 360 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'grey.200' }}>
                                        {['#', 'Code', 'Description', 'Unit', 'Qty', 'Rate', 'GST', 'Amount'].map(h => {
                                            const alignRight = ['Qty', 'Rate', 'GST', 'Amount'].includes(h) ? 'right' : 'left';
                                            const cellContent = (h === 'Rate' || h === 'Amount') ? <>{h}<br /><Typography sx={{mr: 1}}>₹</Typography></> : h;

                                            return (
                                                <TableCell key={h} align={alignRight} sx={{ fontWeight: 600, color: 'text.primary', py: 1.5 }} > {cellContent} </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.values(cartItems).map(({ product, qty }, i) => (
                                        <TableRow key={product.Id} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' }, }} >
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell>{product.itemCode}</TableCell>
                                            <TableCell>{product.description}</TableCell>
                                            <TableCell>{product.unit}</TableCell>
                                            <TableCell align="right">{qty}</TableCell>
                                            <TableCell align="right">{product.saleRate}</TableCell>
                                            <TableCell align="right">{product.TaxGST}%</TableCell>
                                            <TableCell align="right">{(qty * product.saleRate)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                                <TableFooter>
                                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                                        <TableCell colSpan={4}>
                                            <Typography variant="body2" fontWeight={500}> Total Quantity </Typography>
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600 }}> {Object.values(cartItems).reduce((sum, { qty }) => sum + qty, 0)} </TableCell>
                                        <TableCell colSpan={3} />
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>

                        {/* Totals & Place Order */}
                        <Box px={3} py={2}>
                            <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle1">Basic Amount</Typography>
                                <Typography variant="subtitle1">₹{total.toFixed(2)}</Typography>
                            </Grid>

                            {/* Discount Input */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2">Discount (%)</Typography>
                                    <TextField size="small" type="number" inputProps={{ min: 0, max: 100, step: '0.01' }} value={discount} sx={{ width: 80 }}
                                        onChange={(e) => {
                                            let val = parseFloat(e.target.value);
                                            if (isNaN(val)) val = 0;
                                            if (val < 0) val = 0;
                                            if (val > 100) val = 100;
                                            setDiscount(val);
                                        }}
                                    />
                                </Box>
                                <Typography variant="h6" fontWeight={600}>
                                    ₹{(total * (discount / 100)).toFixed(2)}
                                </Typography>
                            </Box>

                            {/* Tax Breakdown (GST) */}
                            {selectedFirm?.state && normalizedState && companyState && (
                                <>
                                    {normalizedState === companyState ? (
                                        <>
                                            <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                                <Typography variant="body2">CGST</Typography>
                                                <Typography variant="body2">
                                                    ₹{Object.values(cartItems).reduce((sum, { product, qty }) => {
                                                        const base = product.saleRate * qty;
                                                        const discountAmt = base * (discount / 100);
                                                        const taxable = base - discountAmt;
                                                        const cgst = (product.TaxGST / 2 / 100) * taxable;
                                                        return sum + cgst;
                                                    }, 0).toFixed(2)}
                                                </Typography>
                                            </Grid>
                                            <Grid container justifyContent="space-between" alignItems="center" mb={2}>
                                                <Typography variant="body2">SGST</Typography>
                                                <Typography variant="body2">
                                                    ₹{Object.values(cartItems).reduce((sum, { product, qty }) => {
                                                        const base = product.saleRate * qty;
                                                        const discountAmt = base * (discount / 100);
                                                        const taxable = base - discountAmt;
                                                        const sgst = (product.TaxGST / 2 / 100) * taxable;
                                                        return sum + sgst;
                                                    }, 0).toFixed(2)}
                                                </Typography>
                                            </Grid>
                                        </>
                                    ) : (
                                        <Grid container justifyContent="space-between" alignItems="center" mb={2}>
                                            <Typography variant="body2">IGST</Typography>
                                            <Typography variant="body2">
                                                ₹{Object.values(cartItems).reduce((sum, { product, qty }) => {
                                                    const base = product.saleRate * qty;
                                                    const discountAmt = base * (discount / 100);
                                                    const taxable = base - discountAmt;
                                                    const igst = (product.TaxGST / 100) * taxable;
                                                    return sum + igst;
                                                }, 0).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                    )}
                                </>
                            )}

                            {/* Round Off */}
                            {(() => {
                                const discountedTotal = total * (1 - discount / 100);
                                const gstTotal = Object.values(cartItems).reduce((sum, { product, qty }) => {
                                    const base = product.saleRate * qty;
                                    const discountAmt = base * (discount / 100);
                                    const taxable = base - discountAmt;
                                    return sum + ((product.TaxGST / 100) * taxable);
                                }, 0);
                                const finalAmount = discountedTotal + gstTotal;
                                const roundedAmount = Math.ceil(finalAmount);
                                const roundOff = +(roundedAmount - finalAmount).toFixed(2);
                                return (
                                    <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="body2">Round Off (+)</Typography>
                                        <Typography variant="body2">₹{roundOff}</Typography>
                                    </Grid>
                                );
                            })()}

                            <Divider sx={{ my: 2 }} />

                            {/* Final Total */}
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Typography variant="h6" fontWeight={700}>Total Amount</Typography>
                                <Typography variant="h6" fontWeight={700}>
                                    ₹{Math.ceil(
                                        total * (1 - discount / 100) +
                                        Object.values(cartItems).reduce((sum, { product, qty }) => {
                                            const base = product.saleRate * qty;
                                            const discountAmt = base * (discount / 100);
                                            const taxable = base - discountAmt;
                                            return sum + ((product.TaxGST / 100) * taxable);
                                        }, 0)
                                    ).toFixed(2)}
                                </Typography>
                            </Grid>

                            {/* CTA */}
                            <Button variant="contained" color="primary" fullWidth size="large" onClick={placeOrder} disabled={loading || !selectedFirm} sx={{ mt: 3, borderRadius: 2 }} >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Place Order'}
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}